import styles from "./PackingGroup.module.css";
import { usePrinterContext } from "@/contexts/PrinterContext";
import { FbaBatchItem, FbaBox, FbaShipment, FbaShipmentItem, getFbaBatchItemById, getItemAssignedQuantity, useShipmentContext } from "@/contexts/ShipmentContext";
import IconBoxPlaceItem from "@/components/icons/IconBoxPlaceItem";
import InputNumber from "@/components/forms/InputNumber/InputNumber";
import Button from "@/components/forms/Button";
import PackingGroupItem from "./PackingGroupItem";
import FbaAssignModal from "../FbaAssignModal/FbaAssignModal";
import { PrinterContextProps } from "@/contexts/PrinterContext";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import Checkbox from "@/components/forms/Checkbox/Checkbox";
import IconEditGoogle from "@/components/icons/IconEditGoogle";
import IconDelete from "@/components/icons/iconDelete";
import IconBarcode from "@/components/icons/IconBarcode";
import Flex from "@/components/Flex/Flex";
import ProductView from "../ProductView";
import { useMemo, useRef, useState } from "react";
import { BoxUpdate, BoxState, BoxesState, AssignDialogProps } from "./PrepAndBoxemPage";
import { BoxContentsProvider, FBAItem, PackingGroup, ShipmentUpdateBoxBody } from "@boxem/core-api-schema";
import { updateBoxV2 } from "@/api/fulfillment";
import { Tooltip } from "@/components/Tooltip";
import TextInput from "@/components/forms/TextInput/TextInput";
import IconSearch from "@/components/icons/IconSearch";
import { filterMap } from "@/utils";
import { DraggingState } from "@/app/fba/FbaShipmentFlow/FbaShipmentFlow/DragNDrop";
import { BoxHeader } from "../ShipmentBox";
import IconPrintGoogle from "@/components/icons/IconPrintGoogle";
import Card, { CardAccordion, CardContent, CollapseButton } from "@/components/Card";
import { useUserContext } from "@/contexts/UserContext";
import { SessionStorageKeys } from "@/utils/sessionStorageUtils";
import StandardModal from "@/components/StandardModal";
import IconMore from "@/components/icons/IconMore";
import Menu, { Option } from "@/components/Menu";

export type PackingGroupViewProps = {
  shipment?: FbaShipment,
  boxesState: BoxesState;
  boxesLoading: boolean,
  packingGroup: PackingGroup;
  index?: number;
  createBoxes: (createBoxCount: number, select?: boolean) => Promise<void>;
  toggleSelectBox: (bs: BoxState) => void;
  toggleBoxOpen: (bs: BoxState) => void;
  selectAllBoxes: (pgId: string, allChecked: boolean) => void;
  deleteBox: (bs: BoxState) => Promise<void>;
  editBox: (box: FbaBox) => void;
  showBulkAssignModal: (props: AssignDialogProps) => void;
  showAssignAllSkusModal: (props: AssignDialogProps) => void;
  onBoxesUpdated: (updates: BoxUpdate[]) => void;
  onError: (e: any) => void;
  draggingState: DraggingState;
};

export interface Item {
  packingItem: FBAItem;
  shipmentItem: FbaShipmentItem;
  batchItem: FbaBatchItem;
  quantity: number;
  assigned: number;
  done: boolean;
}

export default function PackingGroupView(props: PackingGroupViewProps) {
  const {
    shipment,
    boxesState,
    boxesLoading,
    packingGroup: pg,
    index: pgIndex,
    createBoxes,
    toggleSelectBox,
    toggleBoxOpen,
    selectAllBoxes: _selectAllBoxes,
    deleteBox,
    editBox,
    showBulkAssignModal,
    showAssignAllSkusModal,
    onBoxesUpdated,
    onError,
    draggingState
  } = props;

  const { currentBatch: batch } = useShipmentContext();
  const { openPrintFNSKULabelModal, printFNSKULabel } = usePrinterContext();
  const [itemToAssign, setItemToAssign] = useState<Item>(null);
  const [createBoxCount, setCreateBoxCount] = useState(1);

  const [searchInputText, setSearchInputText] = useState<string>("");

  const pgId = pg.inboundPackingGroupId;
  const totalUnits = (shipment ?? pg).items.reduce((acc, item) => acc + item.quantity, 0);
  const boxes = batch.boxes
                  .filter(box => box.packingGroupId === pgId && box.shipmentId == shipment?.id)
                  .sort((a,b)=> {return a.displayIndex < b.displayIndex ? -1 : (a.displayIndex == b.displayIndex) ? 0 : 1});

  const selectedBoxes = useRef(null);
  selectedBoxes.current = boxes.filter(box => boxesState[box.id]?.selected);

  const itemsWithoutAssignedInfo: Omit<Item, "assigned"|"done">[] = useMemo(() => (
    filterMap(pg.items, packingItem => {
      const shipmentItem = shipment?.items.find(si => si.sku === packingItem.msku);
      if (shipment === undefined || shipmentItem !== undefined) return {
        packingItem,
        shipmentItem,
        batchItem: batch.items.find(i => i.sku === packingItem.msku),
        quantity: shipment ? shipmentItem.quantity : packingItem.quantity,
      };
    })
  ), [shipment?.items, pg?.items]);

  const items: Item[] = useMemo(() => (
    itemsWithoutAssignedInfo.map(item => {
      const assigned = getItemAssignedQuantity(batch, pgId, shipment?.id, item.packingItem.msku);
      const quantity = item.shipmentItem?.quantity ?? item.quantity;
      return { ...item, assigned, done: assigned >= quantity };
    }).sort((l, r) => l.done === r.done ? 0 : l.done ? 1 : -1)
  ), [itemsWithoutAssignedInfo, batch?.itemAssignedQuantities]);

  const itemsByBatchItemId = useMemo<{[key: number]: Item}>(() => {
    const map = {};
    for (const item of items) {
      map[item.batchItem.id] = item;
    }
    return map;
  }, [items]);

  const allAssigned = useMemo(() => items.find(item => !item.done) == null, [items]);

  const allChecked = selectedBoxes.current.length === boxes.length;
  const selectAllBoxes = () => _selectAllBoxes(pgId, allChecked);
  const onAssignClick = (item: FbaBatchItem, quantity?: number) => addToBoxes(
    pg,
    [{item, quantity}],
    selectedBoxes.current,
    onError,
    batch.printFNSKUAfterIncreaseQuantityInBox ? printFNSKULabel : null,
  ).then(onBoxesUpdated);
  const onAssignDragNDrop = (item : FbaBatchItem, quantity: number, targetBoxId: number) => {
    const boxes: FbaBox[] = batch.boxes.filter(box => targetBoxId == box.id);
    addToBoxes(
      pg,
      [{item, quantity}],
      boxes,
      onError,
      batch.printFNSKUAfterIncreaseQuantityInBox ? printFNSKULabel : null,
    ).then(onBoxesUpdated);
  };
  const openAssignDialog = (item: FbaBatchItem) => setItemToAssign(itemsByBatchItemId[item.id]);
  const removeItemFromBox = (item: FbaBatchItem, box: FbaBox) => removeFromBox(item, box, onError).then(onBoxesUpdated);

  const searchByText: string = "product name, category, condition, SKU, FNSKU, or ASIN";
  let displayEmptySearchIndicator = searchInputText ? true : false;

  let itemsToDisplay = (
    <div className={styles.scrollArea}>
      {items.map(({packingItem, batchItem, quantity, assigned}) => {
        let searchString = `${batchItem.name} ${batchItem.category} ${batchItem.condition.replace(/_/g," ")} ${batchItem.sku} ${packingItem.fnsku} ${packingItem.asin}`;
        searchString = searchString.toLowerCase();
        // @TODO(michaelp): Better searching than .includes():
        // Allow searching item "Green stuffed animal" by typing "Grstunimal".
        // Basically you iterate characters for a "run" and add to the "cost" for every matched character. When the run breaks, multiply a global multiplier by 0.75 (tunable value) and look for where the run on search string will continue, add to cost for each value. 
        let displayBecauseSearch = !searchInputText || searchString.includes(searchInputText.toLowerCase());
        if(displayBecauseSearch) {
          displayEmptySearchIndicator = false;
          return (
            <PackingGroupItem
              key={`${batchItem.id}+${shipment?.id}`}
              item={batchItem}
              quantity={quantity}
              quantityAssigned={assigned}
              selectedBoxes={selectedBoxes.current}
              prepInstructions={packingItem.prepInstructions}
              onAssignClick={onAssignClick}
              onAssignDragNDrop={onAssignDragNDrop}
              openAssignDialog={openAssignDialog}
              draggingState={draggingState}
              onPrintFNSKUClick={() => {
                openPrintFNSKULabelModal({
                  fnsku: packingItem.fnsku,
                  productName: batchItem.name,
                  expirationDate: batchItem.expirationDate,
                  quantity,
                  productCondition: batchItem.condition
                });
              }}
            />
          )
        } else return false;
      })}
    </div>
  );

  if(displayEmptySearchIndicator) {
    itemsToDisplay = (
      <div style={{display: "flex", padding: "17px 36px var(--space-spacing-25-rem, 40px) 36px", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: "var(--space-spacing-15-rem, 24px)", alignSelf: "stretch"}}>
        <div style={{ color: "var(--static-text-strong, var(--static-text-strong, #1A1A1A))", textAlign: "center", /* Heading/Small Heading */ fontFamily: "Inter", fontSize: "18px", fontStyle: "normal", fontWeight: "600", lineHeight: "normal"}}>
          No products Found
        </div>
        <div style={{color: "var(--static-text-weak, #767676)", textAlign: "center"}}>{"Make sure you are typing the right " + searchByText}</div>
      </div>);
  }

  return (
    <div className={styles.packingGroup}>
      <div className={styles.pgHeader}>
        <div className={styles.leftPane}>
          <IconBoxPlaceItem size={32} style={{ color: 'var(--color-primary)' }}/>
          {totalUnits} Total Units
          <Tooltip title={allAssigned ? "All items have been assigned to boxes" : ""}>
            <Button
              type="black"
              onClick={() => showBulkAssignModal({packingGroupId: pg.inboundPackingGroupId, index: pgIndex, items})}
              disabled={allAssigned}
            >
              Bulk Assign
            </Button>
          </Tooltip>
          <Tooltip title={allAssigned ? "All items have been assigned to boxes" : ""}>
            <Button
              type="black"
              onClick={() => showAssignAllSkusModal({packingGroupId: pg.inboundPackingGroupId, index: pgIndex, items})}
              disabled={allAssigned}
            >
              Assign All SKUs
            </Button>
          </Tooltip>
        </div>
        <div className={styles.rightPane}>
          <label className={styles.selectAllBoxes}>
            <Checkbox
              value={allChecked}
              onChange={selectAllBoxes}
            />
            <span>{allChecked ? "De-select" : "Select"} all boxes</span>
          </label>
          <Button type="black" disableWhileOnClick={true} onClick={() => createBoxes(createBoxCount)}>
            Create New {createBoxCount === 1 ? "Box" : "Boxes"}
          </Button>
          <InputNumber
            className={styles.inputNumber}
            value={createBoxCount}
            onChange={setCreateBoxCount}
            min={1}
            max={999}
          />
        </div>
      </div>
      <div className={styles.pgBody}>
        <div className={styles.leftPane}>
          <Flex gap={4}>
            <TextInput
              placeholder={"Search by " + searchByText}
              prefix={<Flex align="center" style={{marginRight:"1ch"}}><IconSearch /></Flex>}
              value={searchInputText}
              onChange={v => {
                setSearchInputText(v);
              }}
              maxLength={300}
              inputAutofocus
              xButtonToClear={true}
            />
          </Flex>
          {itemsToDisplay}
        </div>
        <div className={styles.rightPane}>
          <div className={styles.boxes}>
            {boxesLoading && <LoadingSpinner style={{ margin: "28px auto" }} />}
            {!boxesLoading && boxes.map(box => (
              <Box
                key={box.id}
                boxState={boxesState[box.id]}
                box={box}
                shipment={shipment}
                batchItems={batch.items}
                showReassign={boxes.length > 1}
                showDelete={boxes.length > 1}
                toggleSelectBox={toggleSelectBox}
                toggleBoxOpen={toggleBoxOpen}
                deleteBox={deleteBox}
                editBox={editBox}
                removeItemFromBox={removeItemFromBox}
                reassignItem={openAssignDialog}
              />
            ))}
            {!boxesLoading && !boxes.find(box => box.packingItems.length > 0) &&
              <div className={styles.emptyBoxContainer}>
                <img src="/images/isometric-open-box.svg" height="100" />
                {!!boxes.find(box => box.packingGroupId === pgId)
                  ? <>
                    <h3 className={styles.emptyHeader}>There are no products in the Box</h3>
                    <span className={`small-text`}>
                      To assign products to boxes, select a box, enter the item quantity, and click "Assign" in the left column.
                    </span>
                  </>
                  : <>
                    <h3 className={styles.emptyHeader}>Create a box</h3>
                    <span className={`small-text`}>You haven't created any boxes for this pack group yet.</span>
                  </>
                }
              </div>
            }
          </div>
        </div>
      </div>
      <FbaAssignModal
        createBoxes={n => createBoxes(n, true)}
        item={itemToAssign}
        boxes={boxes}
        packingGroupId={pgId}
        onClose={() => setItemToAssign(null)}
      />
    </div>
  );
};

type BoxProps = {
  shipment?: FbaShipment;
  boxState: BoxState;
  box: FbaBox;
  batchItems: FbaBatchItem[];
  showReassign: boolean;
  showDelete: boolean;
  toggleSelectBox: (boxState: BoxState) => void;
  toggleBoxOpen: (boxState: BoxState) => void;
  deleteBox: (boxState: BoxState) => Promise<void>;
  removeItemFromBox: (item: FbaBatchItem, box: FbaBox) => Promise<void>;
  editBox: (box: FbaBox) => void;
  reassignItem: (item: FbaBatchItem) => void;
};

function Box({
  shipment,
  box: _box,
  boxState,
  toggleSelectBox,
  toggleBoxOpen,
  deleteBox,
  removeItemFromBox,
  editBox,
  reassignItem,
  showReassign,
  showDelete,
}: BoxProps) {
  if (!boxState) return;

  const { currentBatch } = useShipmentContext();
  const { printFBABoxLabels } = usePrinterContext();
  const { addressBook } = useUserContext();
  const [collapsed, setCollapsed] = useState(!boxState.open);
  const [showLabelsWarning, setShowLabelsWarning] = useState(false);

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
    toggleBoxOpen(boxState);
  };

  const box = useRef<FbaBox>(_box);
  box.current = _box;

  const isEmpty = box.current.packingItems.length === 0;
  const isOpen = !collapsed;

  const printBoxLabel = (confirmed?: boolean) => {
    if (!confirmed && currentBatch.boxContentsProvider === BoxContentsProvider.Barcode2D) {
      setShowLabelsWarning(true);
      return;
    }
    const fromAddress = addressBook.find(a => a.id === shipment!.fromId);
    printFBABoxLabels(
      shipment!,
      fromAddress,
      [box.current],
      currentBatch.boxContentsProvider === BoxContentsProvider.Barcode2D,
      currentBatch,
    );
  };

  const onLabelsWarningClosed = (confirmed: boolean) => {
    setShowLabelsWarning(false);
    if (confirmed) {
      printBoxLabel(true);
    }
  };

  return (
    <Card active={boxState.selected} data-box-id-prep-n-boxem={box.current.id?.toString()}>
      <CardContent className={styles.boxHeader} borderBottom="solid">
        <label>
          <Checkbox value={boxState.selected} onChange={() => toggleSelectBox(boxState)}/>
          <BoxHeader style={{flex: 1}} box={box.current} />
        </label>
        <div className={styles.boxHeaderButtons}>
          {!isEmpty && shipment &&
            <Tooltip title="Print FBA Box Label">
              <Button type="text" onClick={() => printBoxLabel()}>
                <IconPrintGoogle size={24} className={styles.iconButton} />
              </Button>
            </Tooltip>
          }
          <Button
            type="text"
            onClick={() => editBox(box.current)}
          >
            <IconEditGoogle size={24} className={styles.iconButton} />
          </Button>
          {showDelete &&
            <Button
              type="text"
              onClick={() => deleteBox(boxState)}
              disableWhileOnClick={true}
            >
              <IconDelete size={24} className={styles.iconButton} />
            </Button>
          }
        </div>
        <div className={styles.boxHeaderMenu}>
          <Menu button={<IconMore size={24} className={styles.iconButton} />}>
            {!isEmpty && shipment &&
              <Option onClick={() => printBoxLabel()}>
                <IconPrintGoogle size={24} className={styles.iconButton} />
                Print FBA box label
              </Option>
            }
            <Option onClick={() => editBox(box.current)}>
              <IconEditGoogle size={24} className={styles.iconButton} />
              Set dimensions & weight
            </Option>
            {showDelete &&
              <Option onClick={() => deleteBox(boxState)}>
                <IconDelete size={24} className={styles.iconButton} />
                Delete box
              </Option>
            }
          </Menu>
        </div>
        {!isEmpty && <CollapseButton collapsed={!isOpen} onChange={toggleCollapsed} />}
      </CardContent>
      <CardAccordion collapsed={!isOpen}>
        {box.current.packingItems.map(item =>
          <BoxItem
            key={item.batchItemId}
            item={getFbaBatchItemById(currentBatch, item.batchItemId)}
            quantity={item.quantity}
            showReassign={showReassign}
            removeItemFromBox={(item: FbaBatchItem) => removeItemFromBox(item, box.current)}
            reassignItem={reassignItem}
          />
        )}
      </CardAccordion>
      <StandardModal
        open={showLabelsWarning}
        icon={IconBarcode}
        onClose={() => onLabelsWarningClosed(false)}
        onSubmit={() => onLabelsWarningClosed(true)}
        title="Early Printing of FBA Box Labels with 2D Barcodes"
        submitText="I Acknowledge"
        submitSentiment="danger"
        rememberDecisionKey={SessionStorageKeys.skipPrintFBABoxLabelModalConfirmation}
        body={`
          Boxem enables you to print your FBA Box labels early while you prep your boxes. Each FBA box label's
          2-D barcode captures the exact SKUs and quantities logged for that box at the moment of printing. If
          you add, remove, or change any units afterward, the 2D Barcode becomes invalid. If you print the FBA
          box label and then change the box contents, discard or cover the old FBA box label and print a new
          FBA box label before shipping.
        `}
      />
    </Card>
  )
}

type BoxItemProps = {
  item: FbaBatchItem;
  quantity: number;
  showReassign: boolean;
  removeItemFromBox: (item: FbaBatchItem) => Promise<void>;
  reassignItem: (item: FbaBatchItem) => void;
}

function BoxItem({item, quantity, showReassign, removeItemFromBox, reassignItem}: BoxItemProps) {
  return (
    <CardContent className={styles.boxItem} borderBottom="dashed">
      <ProductView
        item={item}
        quantity={quantity}
        small={true}
        lines={3}
        showAsin={false}
        showCondition={false}
        showSku={false}
        content1={
          <div className={styles.boxButtons}>
            <Button
              onClick={() => removeItemFromBox(item)}
              disableWhileOnClick={true}
              type="secondary"
              size="small"
            >
              {quantity} Units <IconDelete size={16} />
            </Button>
            <Button
              onClick={() => reassignItem(item)}
              type="secondary"
              size="small"
              style={{ visibility: showReassign ? "visible" : "hidden" }}
            >
              Reassign
            </Button>
          </div>
        }
      />
    </CardContent>
  );
}

export async function addToBoxes(
  packingGroup: PackingGroup,
  items: { item: FbaBatchItem, quantity: number }[],
  boxes: FbaBox[],
  onError: (arg0: any) => void,
  printFNSKULabel?: PrinterContextProps["printFNSKULabel"] | null
): Promise<BoxUpdate[]> {
  const requests: Promise<{error?: any}>[] = [];
  const labelCount = {};
  const allUpdates = [];
  for (const [i, box] of boxes.entries()) {
    const updates = [];
    const dto: ShipmentUpdateBoxBody = {
      items: box.packingItems.map(item => ({...item})),
      packingGroupId: box.packingGroupId,
      shipmentId: box.shipmentId,
    };
    for (const {item, quantity} of items) {
      const n = Math.floor(quantity / boxes.length);
      const rem = quantity % boxes.length;
      const quantityInBox = n + (i < rem ? 1 : 0);

      if (quantityInBox < 1) continue;

      labelCount[item.id] = 0;

      let itemInBox = dto.items.find(k => k.sku === item.sku || k.batchItemId === item.id);
      let newItem = false;
      if (!itemInBox) {
        newItem = true;
        itemInBox = {
          batchItemId: item.id,
          sku: item.sku,
          quantity: 0,
        };
        dto.items.push(itemInBox);
      }
      itemInBox.quantity += quantityInBox;
      if (printFNSKULabel) labelCount[item.id] += quantityInBox;
      updates.push({ newItem, quantity: itemInBox.quantity, item, boxId: box.id });
    }
    requests.push(updateBoxV2(box.id, dto).then(() => {
      allUpdates.push(...updates);
      return {};
    }).catch(e => ({ error: e })));
  }
  const results = await Promise.all(requests);
  const errors = results.filter(result => !!result.error)
    .reduce((acc, result) => { acc.push(...(Array.isArray(result.error) ? result.error : [result.error])); return acc }, [])
  
  if (errors.length > 0) onError(errors);

  for (const {item} of items) {
    if (labelCount[item.id] > 0) {
      const packingItem = packingGroup.items.find(k => k.msku === item.sku); 
      printFNSKULabel(
        packingItem.fnsku,
        item.name,
        item.condition,
        labelCount[item.id],
        item.expirationDate
      );
    }
  }

  return allUpdates;
}

async function removeFromBox(
  item: FbaBatchItem,
  box: FbaBox,
  onError: (any) => void,
): Promise<BoxUpdate[]> {
  const dto: ShipmentUpdateBoxBody = {
    items: box.packingItems.filter(pi => pi.batchItemId !== item.id && pi.sku !== item.sku),
    packingGroupId: box.packingGroupId,
    shipmentId: box.shipmentId,
  };
  const result: BoxUpdate | { error: any } = await updateBoxV2(box.id, dto)
    .then(() => ({ removedItem: true, quantity: 0, item, boxId: box.id }))
    .catch(e => ({ error: e }))

  if ((result as any).error) {
    onError(Array.isArray((result as any).error) ? (result as any).error : [(result as any).error]);
    return [];
  }
  return [result as BoxUpdate];
}
